import React, { useState, useEffect } from 'react';
import { fetchClaimOrders } from '../services/api';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const d120 = new Date(Date.now() - 120 * 86400000).toISOString().split('T')[0];
const PAGE_SIZE = 50;

export default function ClaimOrders({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(d120);
  const [end, setEnd] = useState(todayIso);
  const [claimNumber, setClaimNumber] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    const params = {
      StartDate: toApiDate(start),
      EndDate: toApiDate(end),
      PageNumber: page,
      PageSize: PAGE_SIZE,
      ...(claimNumber ? { ClaimNumber: claimNumber } : {}),
      ...(trackingNumber ? { TrackingNumber: trackingNumber } : {}),
    };
    fetchClaimOrders(params)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const totalBalance = rows.reduce((s, r) => s + Number(r.claimBalance ?? r.balance ?? 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Claim Orders Report</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>FROM</div>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TO</div>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>CLAIM #</div>
            <input type="text" value={claimNumber} onChange={e => setClaimNumber(e.target.value)} placeholder="Optional" className="form-input" style={{ fontSize: 11, width: 110 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TRACKING #</div>
            <input type="text" value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Optional" className="form-input" style={{ fontSize: 11, width: 130 }} />
          </div>
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Total Claims', value: total.toLocaleString() },
            { label: 'Total Claim Balance', value: `$${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
          ].map(({ label, value }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Claims {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No claim orders found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Claim #</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Tracking #</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Balance</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.customerName ?? '—')}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.claimNumber ?? '—'}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.trackingNumber ?? '—'}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>${Number(row.claimBalance ?? row.balance ?? 0).toFixed(2)}</td>
                      <td style={{ padding: '8px 8px' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: '#e8f0fe', color: '#00395C' }}>
                          {row.status ?? 'Open'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {row.startDate ?? row.orderDate ? new Date(row.startDate ?? row.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
